import { Box, CircularProgress, InputBase, Stack, Tooltip, Typography } from '@mui/material';
import { BarChart3, File, Pencil, Trash2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { ButtonStd } from '../ui/ButtonStd';

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) {
    const hrs = Math.floor(diff / 3600000);
    return hrs < 1 ? 'Just now' : `${hrs}h ago`;
  }
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const FileInspector = ({ file, onOpen, onDelete, onRenamed }) => {
  const currentProjectId = useProjectStore((state) => state.currentProjectId);
  const renameFile = useProjectStore((state) => state.renameFile);
  // Read from prefetched cache in projectStore
  const fileInfo = useProjectStore((state) => (file ? state.fileInfoCache[file.filename] : null));
  const history = useProjectStore((state) => (file ? state.fileHistoryCache[file.filename] : null));
  const loadingInfo = file && !fileInfo;
  const loadingHistory = file && !history;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [renameError, setRenameError] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Derive stem and extension from the user-facing display name
  const displayName = file?.display_name || file?.filename || '';
  const dotIdx = displayName.lastIndexOf('.');
  const currentStem = dotIdx > 0 ? displayName.slice(0, dotIdx) : displayName;
  const displayExt = dotIdx > 0 ? displayName.slice(dotIdx) : '';

  // Cancel edit if the selected file changes
  useEffect(() => {
    setEditing(false);
    setDraft('');
    setRenameError(null);
  }, [file?.filename]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    setDraft(currentStem);
    setRenameError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
    setRenameError(null);
  };

  const commitEdit = async () => {
    const newStem = draft.trim();
    if (!newStem || newStem === currentStem) {
      cancelEdit();
      return;
    }
    if (/[\\/.]/.test(newStem)) {
      setRenameError('No dots, slashes, or backslashes');
      return;
    }
    setSaving(true);
    try {
      const result = await renameFile(file.filename, newStem);
      setEditing(false);
      setDraft('');
      setRenameError(null);
      onRenamed?.(file.filename, result.filename);
    } catch (e) {
      setRenameError(e.response?.data?.detail || e.message || 'Rename failed');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  if (!file) {
    return (
      <Box
        sx={{
          flex: 1,
          bgcolor: '#E9ECED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Typography sx={{ color: '#7d8d97', fontSize: 13, textAlign: 'center' }}>
          Select a file to inspect
        </Typography>
      </Box>
    );
  }

  const sizeDisplay = file.size_bytes ? `${(file.size_bytes / 1024 / 1024).toFixed(1)} MB` : '—';

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: '#E9ECED',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header: file name + action buttons */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <File size={20} strokeWidth={1.5} color="#394b57" style={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <InputBase
                inputRef={inputRef}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setRenameError(null);
                }}
                onBlur={commitEdit}
                onKeyDown={handleKeyDown}
                disabled={saving}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: '#394b57',
                  fontWeight: 600,
                  fontSize: 15,
                  '& input': {
                    p: 0,
                    bgcolor: '#fff',
                    border: '1px solid #C4CDD3',
                    borderRadius: 0.5,
                    px: 0.75,
                    py: 0.25,
                  },
                }}
              />
              <Typography sx={{ color: '#7d8d97', fontWeight: 600, fontSize: 15, flexShrink: 0 }}>
                {displayExt}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover .rename-icon': { opacity: 1 },
              }}
              onClick={startEdit}
            >
              <Typography
                sx={{
                  color: '#394b57',
                  fontWeight: 600,
                  fontSize: 15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </Typography>
              <Tooltip title="Rename">
                <Box
                  className="rename-icon"
                  sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', flexShrink: 0 }}
                >
                  <Pencil size={13} strokeWidth={1.8} color="#7d8d97" />
                </Box>
              </Tooltip>
            </Box>
          )}
          <Typography sx={{ color: renameError ? '#d32f2f' : '#7d8d97', fontSize: 11 }}>
            {renameError || (file.filename.endsWith('.dld9') ? 'DLD9 Measurement' : 'Data File')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <ButtonStd height="30px" onClick={() => onOpen?.(file)}>
            Open
          </ButtonStd>
          <ButtonStd height="30px" onClick={() => onDelete?.(file)}>
            Delete
          </ButtonStd>
        </Stack>
      </Box>

      {/* Scrollable body: details then history */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        {/* ── File ─────────────────────────────────────── */}
        <Box sx={{ maxWidth: 400 }}>
          <SectionTitle>File</SectionTitle>
          <Stack spacing={0.75}>
            <MetaRow label="Size" value={sizeDisplay} />
            <MetaRow
              label="Modified"
              value={
                file.modified_at
                  ? new Date(file.modified_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '—'
              }
            />
          </Stack>
        </Box>

        {/* ── Measurement ────────────────────────────────── */}
        <Box sx={{ mt: 2.5, maxWidth: 400 }}>
          {loadingInfo && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={16} sx={{ color: '#7d8d97' }} />
            </Box>
          )}
          {fileInfo && !loadingInfo && (
            <Stack spacing={0.75}>
              <SectionTitle>Measurement</SectionTitle>
              <MetaRow label="Device" value={fileInfo.device_type || '—'} />
              <MetaRow label="Chambers" value={fileInfo.chamber_count} />
              <Box>
                <Typography sx={{ color: '#7d8d97', fontSize: 13, mb: 0.25 }}>Plots</Typography>
                {fileInfo.data_types?.length > 0 ? (
                  <Stack spacing={0.25} alignItems="flex-end">
                    {fileInfo.data_types.map((type) => (
                      <Typography
                        key={type}
                        sx={{ color: '#394b57', fontSize: 13, fontWeight: 600 }}
                      >
                        {type}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography
                    sx={{ color: '#394b57', fontSize: 13, fontWeight: 600, textAlign: 'right' }}
                  >
                    —
                  </Typography>
                )}
              </Box>
              {fileInfo.duration_seconds != null && (
                <MetaRow label="Duration" value={formatDuration(fileInfo.duration_seconds)} />
              )}
              {fileInfo.sample_count != null && (
                <MetaRow label="Data points" value={fileInfo.sample_count.toLocaleString()} />
              )}
              {fileInfo.mark_count != null && <MetaRow label="Marks" value={fileInfo.mark_count} />}
              {fileInfo.event_count != null && (
                <MetaRow label="Events" value={fileInfo.event_count} />
              )}
            </Stack>
          )}
        </Box>

        {/* ── History ─────────────────────────────────── */}
        <Box sx={{ mt: 2.5, maxWidth: 400 }}>
          <SectionTitle>History</SectionTitle>
          {loadingHistory && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={16} sx={{ color: '#7d8d97' }} />
            </Box>
          )}
          {!loadingHistory && history && history.length === 0 && (
            <Typography sx={{ color: '#A9B6BC', fontSize: 12 }}>
              No version history yet — sync to create the first version
            </Typography>
          )}
          {!loadingHistory && history && history.length > 0 && (
            <Stack spacing={0}>
              {history.map((entry, i) => (
                <Box
                  key={entry.commit_hash}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    py: 0.75,
                    borderBottom: i < history.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: i === 0 ? '#00A779' : '#C4CDD3',
                        flexShrink: 0,
                      }}
                    />
                    {i < history.length - 1 && (
                      <Box sx={{ width: 1, flex: 1, bgcolor: '#C4CDD3', mt: 0.5 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontFamily: 'monospace',
                          color: '#7d8d97',
                          bgcolor: 'rgba(0,0,0,0.05)',
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.5,
                        }}
                      >
                        {entry.commit_hash}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#7d8d97' }}>
                        {formatDate(entry.date)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: '#394b57', mb: 0.25 }}>
                      {entry.message}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#A9B6BC' }}>{entry.author}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      color: '#394b57',
      fontSize: 14,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);

const MetaRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography sx={{ color: '#7d8d97', fontSize: 13 }}>{label}</Typography>
    <Typography sx={{ color: '#394b57', fontSize: 13, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export default FileInspector;
